
import { Injectable } from "@angular/core";
import { Apollo } from "apollo-angular";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { NSDomainData, NSFormFields, NSHttpClient } from "../types";
import { LocalAssetRequestService } from "./local-asset-requests.service";
import { RequestService } from "./request.service";
import { ResponseService } from "./response.service";

import { languageListQuery } from "./queries/languages.query";
import { linesOfBusinessListQuery } from "./queries/lines-of-business.query";
import { territoryListQuery } from "./queries/territories.query";

import { includes, sortBy } from "lodash";

@Injectable()
export class GenericRequestService extends RequestService {
    constructor(
        private apollo: Apollo,
        private response: ResponseService,
        private localAsset: LocalAssetRequestService,
    ) {
        super();
    }

    public getGenericLinesOfBusiness(params?): Observable<NSHttpClient.APIResponse<NSDomainData.ILineOfBusiness[]>> {
        return this.apollo
            .query((linesOfBusinessListQuery(params)) as any).pipe(
            map(this.response.formatResponseBody));
    }

    public getGenericTerritories(params?): Observable<NSHttpClient.APIResponse<NSDomainData.ITerritory[]>> {
        return this.apollo
            .query((territoryListQuery(params)) as any).pipe(
            map(this.response.formatResponseBody));
    }

    public getGenericLanguagesDEPRECATED(params?): Observable<NSHttpClient.APIResponse<NSDomainData.ILanguage[]>> {
        return this.apollo
            .query((languageListQuery(params)) as any).pipe(
            map(this.response.formatResponseBody),
            map((body) => {
                const commonLanguages = body.data.filter((lang) => includes(["en", "es", "fr"], lang.code));
                const lessCommonLanguages = body.data.filter((lang) => !includes(["en", "es", "fr"], lang.code));
                return {
                    data: [...commonLanguages, ...lessCommonLanguages],
                };
            }));
    }

    public getGenericVideoFormats(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/video-formats.json");
    }

    public getGenericFrameRates(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/generic-frame-rates.json");
    }

    public getGenericDeliverableEditors(params): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/deliverable-editors.json", params);
    }

    public getGenericEndCreditTypes(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/generic-ec-types.json");
    }

    public getGenericYesNoValueTypes(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/generic-yn-types.json");
    }

    public getGenericEndCreditFormats(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/generic-ec-formats.json");
    }

    public getGenericSeparateAudioFileTracks(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/generic-separate-audio-file-tracks.json");
    }

    public getGenericVarispeeds(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/varispeeds.json");
    }

    public getGenericScreenSizes(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/screensizes.json");
    }

    public getGenericVersionName(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/product-version-name.json");
    }

    public getGenericAspectRatios(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/aspectratios.json").pipe(
            map((res) => {
                return {
                    data: sortBy(res.data).reverse(),
                };
            }));
    }

    public getGenericFacilities(params?): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/facilities.json", params);
    }

    public getGenericLanguages(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/generic-languages.json");
    }

    public getGenericAudioProfileTracks(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/generic-audio-tracks.json");
    }

    public getDeliveryMedia(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/delivery-media.json");
    }

    public getScreenerTypes(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/screener-types.json");
    }

    public getSeparateCaptionFileTypes(): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/separate-caption-file-types.json");
    }

    public getUSStates(params?): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/states.json", params);
    }

    public getCountries(params?): Observable<NSHttpClient.APIResponse<NSFormFields.IChoiceFieldOption[]>> {
        return this.localAsset.getJsonFile("/assets/data/countries.json", params);
    }
}

export const genericRequestProvider = [
    {
        provide: RequestService,
        useClass: GenericRequestService,
        multi: true,
    },
];
